/**
 * ClipboardManager - Handles copy, paste, cut, and duplicate operations
 */

import { EventEmitter } from '../core/EventEmitter';
import { EditableElement } from '../editing/types';

export interface ClipboardManagerOptions {
  pasteOffset?: number;
}

const DEFAULT_OPTIONS: Required<ClipboardManagerOptions> = {
  pasteOffset: 20,
};

export class ClipboardManager {
  private emitter: EventEmitter;
  private options: Required<ClipboardManagerOptions>;
  
  // Clipboard contents
  private clipboard: EditableElement[] = [];
  
  // Callbacks
  private onPaste: ((elements: EditableElement[]) => void) | null = null;
  private onCut: ((elements: EditableElement[]) => void) | null = null;

  constructor(emitter: EventEmitter, options: ClipboardManagerOptions = {}) {
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Set callbacks
   */
  setOnPaste(callback: (elements: EditableElement[]) => void): void {
    this.onPaste = callback;
  }

  setOnCut(callback: (elements: EditableElement[]) => void): void {
    this.onCut = callback;
  }

  /**
   * Copy selected elements to clipboard
   * @param viaContextMenu - If true, indicates copy was done via context menu
   */
  copy(elements: EditableElement[], selectedIds: string[], viaContextMenu: boolean = false): void {
    const selectedElements = elements.filter((el) => selectedIds.includes(el.id));
    if (selectedElements.length === 0) return;
    
    this.clipboard = selectedElements.map((el) => ({ ...el }));
    
    // Store flag to indicate copy was done via context menu
    if (viaContextMenu) {
      (this as any).copiedViaContextMenu = true;
    } else {
      (this as any).copiedViaContextMenu = false;
    }
  }
  
  /**
   * Check if copy was done via context menu
   */
  wasCopiedViaContextMenu(): boolean {
    return (this as any).copiedViaContextMenu === true;
  }

  /**
   * Paste elements from clipboard
   * @param position - Optional position to paste at (cursor position). If not provided, uses offset from original position.
   */
  paste(position?: { x: number; y: number }): EditableElement[] {
    if (this.clipboard.length === 0) return [];
    
    let newElements: EditableElement[];
    
    if (position) {
      // Paste at cursor position
      // Calculate the offset from the first element's original position
      const firstElement = this.clipboard[0];
      const offsetX = position.x - firstElement.x;
      const offsetY = position.y - firstElement.y;
      
      // Apply the same offset to all elements to maintain relative positions
      newElements = this.clipboard.map((el, index) => ({
        ...el,
        id: `${el.id}-copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        x: el.x + offsetX,
        y: el.y + offsetY,
      }));
    } else {
      // Fallback: use offset from original position
      const offset = this.options.pasteOffset;
      newElements = this.clipboard.map((el) => ({
        ...el,
        id: `${el.id}-copy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        x: el.x + offset,
        y: el.y + offset,
      }));
    }
    
    if (this.onPaste) {
      this.onPaste(newElements);
    }
    
    return newElements;
  }

  /**
   * Cut selected elements (copy + delete)
   */
  cut(elements: EditableElement[], selectedIds: string[]): void {
    const selectedElements = elements.filter((el) => selectedIds.includes(el.id));
    if (selectedElements.length === 0) return;
    
    this.clipboard = selectedElements.map((el) => ({ ...el }));
    
    if (this.onCut) {
      this.onCut(selectedElements);
    }
  }

  /**
   * Duplicate selected elements (copy + paste in place)
   */
  duplicate(elements: EditableElement[], selectedIds: string[]): EditableElement[] {
    const selectedElements = elements.filter((el) => selectedIds.includes(el.id));
    if (selectedElements.length === 0) return [];
    
    const offset = this.options.pasteOffset;
    const newElements = selectedElements.map((el) => ({
      ...el,
      id: `${el.id}-duplicate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x: el.x + offset,
      y: el.y + offset,
    }));
    
    if (this.onPaste) {
      this.onPaste(newElements);
    }
    
    return newElements;
  }

  /**
   * Check if clipboard has elements
   */
  hasClipboard(): boolean {
    return this.clipboard.length > 0;
  }

  /**
   * Clear clipboard
   */
  clearClipboard(): void {
    this.clipboard = [];
  }

  /**
   * Get clipboard contents (for inspection)
   */
  getClipboard(): EditableElement[] {
    return [...this.clipboard];
  }

  /**
   * Reset context menu copy flag (called after paste)
   */
  resetContextMenuFlag(): void {
    (this as any).copiedViaContextMenu = false;
  }
  
  /**
   * Destroy the clipboard manager
   */
  destroy(): void {
    this.clipboard = [];
    this.onPaste = null;
    this.onCut = null;
    (this as any).copiedViaContextMenu = false;
  }
}
